import dayjs from 'dayjs';
import type { Duration } from 'dayjs/plugin/duration';
import isPlainObject from 'lodash/isPlainObject';

import { CheckPasswordStatus } from '@webapp/common/lib/api';
import { forceHeader, isAllPagesOnOnePage, isPreview } from '@webapp/common/lib/const';
import { isEmbedded } from '@webapp/common/lib/ua';
import { HeaderBackgroundType, HeaderTitlePosition } from '@webapp/common/lib/ui';
import { UnescapeHtml } from '@webapp/common/lib/utils';
import { CANTRATE_ID } from '@webapp/common/resources/constants';
import type { PageModel, SurveyStore } from '@webapp/common/resources/entities';
import { BundleFinishType, BundleType } from '@webapp/common/resources/entities';
import type { InfoModel } from '@webapp/common/resources/entities/info';
import { AnswerType, checkIsQWithClickReply, QuestionType, WelcomePageType } from '@webapp/common/resources/survey';

import type { QuestionActorRef, QuestionContext } from '@webapp/survey/src/machine/question-machine';

import type { BundleEntity } from '../entities/bundle';

import { calcSurveyProgress } from './helpers/survey';
import { isOfflineMode } from './is-offline-mode';
import { getQ, isQuestionTimerExpired } from './lib';
import { SurveyState } from './states';

const selfAnswersPresent = (q: QuestionContext): boolean => {
    const selfAnswers = q.answersActors.filter((a) => a.getSnapshot().context.type === AnswerType.SELF);

    return selfAnswers.length > 0;
};

export const stateOrSubstate = (state: string | AnyObject, targetState: string): boolean => {
    return state === targetState || (isPlainObject(state) && Object.keys(state).includes(targetState));
};

export const selectWithPassword = (ctx: SurveyStore): boolean => {
    const { individualPasswords, passwordAccess } = ctx.info.params.main;
    return passwordAccess || individualPasswords;
};

export const selectAuthenticatedPwd = (ctx: SurveyStore): boolean =>
    Boolean(typeof ctx?.tokenData?.private === 'object' && ctx?.tokenData?.private?.password === 0);

export const selectAuthenticatedVk = (ctx: SurveyStore): boolean =>
    Boolean(typeof ctx?.tokenData?.private === 'object' && ctx?.tokenData?.private?.vkAuth === 0);

export const selectAskPassword = (ctx: SurveyStore): boolean => {
    const wp = selectWithPassword(ctx);
    const authPwd = selectAuthenticatedPwd(ctx);

    return wp && !authPwd;
};

export const selectAskVkAuth = (ctx: SurveyStore): boolean => {
    const wp = selectWithPassword(ctx);
    const authPwd = selectAuthenticatedPwd(ctx);
    const authVk = selectAuthenticatedVk(ctx);
    const { vkAuth } = ctx.info.params.main;

    return vkAuth && ((wp && authPwd) || (!wp && !authVk));
};

export const selectPwdStatus = (ctx: SurveyStore): CheckPasswordStatus => ctx.pwdCheck;

export const selectCurrentBundle = (ctx: SurveyStore) => {
    const { bundles, currentBundleIndex } = ctx;

    if (bundles.length === 0) {
        return null;
    }

    return bundles[currentBundleIndex];
};

export const selectCurrentVisibleBundle = (ctx: SurveyStore) => {
    const { bundles, currentBundleIndex } = ctx;

    if (bundles.length === 0) {
        return null;
    }

    return bundles[currentBundleIndex];
};

export const selectBundles = (ctx: SurveyStore) => ctx.bundles;

export const selectBundleByIndex = (ctx: SurveyStore, bundleIndex: number) => {
    const { bundles } = ctx;

    if (bundles.length === 0) {
        return null;
    }

    return bundles[bundleIndex];
};

export const selectIsFirstBundle = ({ currentBundleIndex }: SurveyStore): boolean => currentBundleIndex === 0;

export const selectCurrentBundleIndex = ({ currentBundleIndex }: SurveyStore): number => currentBundleIndex;

export const selectPwdError = ({ info, pwdCheck }: SurveyStore): string => {
    let error = null;
    const {
        params: {
            main: { passwordWasActivated, wrongPasswordLabel },
        },
    } = info;

    switch (pwdCheck) {
        case CheckPasswordStatus.ERROR:
            error = wrongPasswordLabel;
            break;
        case CheckPasswordStatus.PASSWORD_ALREADY_USED:
            error = passwordWasActivated;
            break;
        default:
    }

    return error ? UnescapeHtml(error) : null;
};

export const selectDontShowNextButton = (ctx: SurveyStore, bundleIndex: number): boolean => {
    const currentBundle = selectBundleByIndex(ctx, bundleIndex);
    const {
        info: {
            params: {
                other: { clickReply },
            },
        },
    } = ctx;

    return Boolean(
        currentBundle &&
            clickReply &&
            currentBundle.questions.every((q) => {
                const ctx = q.getSnapshot().context;

                if (ctx.type === QuestionType.MAX_DIFF) {
                    const maxDiffCtx = ctx.maxDiffBlocksActor.getSnapshot().context;
                    return (
                        !maxDiffCtx.answers ||
                        maxDiffCtx.blocks.length === 0 ||
                        !maxDiffCtx.blocks.every(
                            (_block, idx) => maxDiffCtx.answers[idx]?.best && maxDiffCtx.answers[idx]?.worst,
                        )
                    );
                }
                if (ctx.type === QuestionType.KANO) {
                    const filtered = ctx.answersActors.filter(
                        (answer) =>
                            !answer.getSnapshot().context.hidden && !answer.getSnapshot().context.hiddenByAnswersHide,
                    );
                    if (filtered.length === 0) return false;
                    return !filtered.every((answer) => {
                        const ctx = answer.getSnapshot().context;
                        const responseCtx = ctx.response?.getSnapshot().context;
                        return (
                            responseCtx &&
                            (responseCtx.value === CANTRATE_ID || (responseCtx.value && responseCtx.extra))
                        );
                    });
                }

                const {
                    params: { comment: commentEnabled, required, testAnswerReaction },
                } = ctx;
                const withSelfAnswer = selfAnswersPresent(ctx);
                const withoutComment = !commentEnabled;

                if (ctx.type === QuestionType.TEST_ONE_OF_LIST) {
                    if (testAnswerReaction && withoutComment) {
                        return true;
                    }
                }

                return (
                    checkIsQWithClickReply(ctx) &&
                    withoutComment &&
                    required &&
                    !withSelfAnswer &&
                    !ctx.responseActor.getSnapshot().context.value
                );
            }),
    );
};

export const selectDontShowNextNextButton = (ctx: SurveyStore, bundleIndex: number): boolean => {
    const currentBundle = selectBundleByIndex(ctx, bundleIndex);
    const {
        info: {
            params: {
                other: { clickReply },
            },
        },
    } = ctx;

    return Boolean(
        currentBundle &&
            clickReply &&
            currentBundle.questions.every((q) => {
                const ctx = q.getSnapshot().context;
                const {
                    params: { comment: commentEnabled },
                } = ctx;
                const withSelfAnswer = selfAnswersPresent(ctx);
                const withoutComment = !commentEnabled;
                return checkIsQWithClickReply(ctx) && withoutComment && !withSelfAnswer;
            }),
    );
};

export const selectNextBundle = ({ bundles, currentBundleIndex }: SurveyStore): BundleEntity => {
    if (bundles.length === 0 || bundles.length === currentBundleIndex + 1) {
        return null;
    }

    return bundles[currentBundleIndex + 1];
};

export const selectNavImpossible = (ctx: SurveyStore, state: SurveyState, bundleIndex: number): boolean => {
    const currentBundle = selectBundleByIndex(ctx, bundleIndex);

    return Boolean(
        !stateOrSubstate(state, SurveyState.bundles) || !currentBundle || currentBundle.type !== BundleType.QUESTIONS,
    );
};

export const selectNextNavImpossible = (ctx: SurveyStore, state: SurveyState): boolean => {
    const currentBundle = selectNextBundle(ctx);

    return Boolean(
        !stateOrSubstate(state, SurveyState.bundles) || !currentBundle || currentBundle.type !== BundleType.QUESTIONS,
    );
};

export const selectNextButtonVisible = (ctx: SurveyStore, state: SurveyState, bundleIndex: number): boolean => {
    const canNot = selectNavImpossible(ctx, state, bundleIndex);
    const nextBundle = selectBundleByIndex(ctx, bundleIndex + 1);

    if (canNot) {
        return false;
    }

    return nextBundle && nextBundle.type === BundleType.QUESTIONS;
};

export const selectPrevNextButtonVisible = (ctx: SurveyStore, state: SurveyState): boolean => {
    const canNot = selectNextNavImpossible(ctx, state);
    const prevBundle = selectCurrentBundle(ctx);
    if (!prevBundle) {
        return false;
    }

    if (canNot || prevBundle.questions.some(isQuestionTimerExpired)) {
        return false;
    }

    return !!prevBundle;
};

// Ближайший предыдущий бандл, на который можно вернуться: бандлы с истёкшим
// таймером вопроса пропускаем — ответить на них уже нельзя.
export const selectPrevAvailableBundleIndex = (ctx: SurveyStore, bundleIndex: number): number => {
    for (let i = bundleIndex - 1; i >= 0; i--) {
        const bundle = selectBundleByIndex(ctx, i);
        if (bundle && !bundle.questions.some(isQuestionTimerExpired)) {
            return i;
        }
    }
    return -1;
};

export const selectPrevButtonVisible = (ctx: SurveyStore, state: SurveyState, bundleIndex: number): boolean => {
    const canNot = selectNavImpossible(ctx, state, bundleIndex);
    if (canNot) {
        return false;
    }

    return selectPrevAvailableBundleIndex(ctx, bundleIndex) >= 0;
};

// На странице с единственным обязательным FIGMA-вопросом кнопки «Далее»/«Отправить»
// не показываются: после прохождения вопроса переход происходит автоматически.
export const selectIsSingleRequiredFigmaBundle = (ctx: SurveyStore, bundleIndex: number): boolean => {
    const bundle = selectBundleByIndex(ctx, bundleIndex);

    if (!bundle || bundle.type !== BundleType.QUESTIONS || bundle.questions.length !== 1) {
        return false;
    }

    const qCtx = bundle.questions[0].getSnapshot().context;

    return qCtx.type === QuestionType.FIGMA_TEST && Boolean(qCtx.params.required);
};

export const selectShowNextButtonByBundleIdx = (ctx: SurveyStore, state: SurveyState, bundleIndex: number): boolean => {
    const dontShowNextButton = selectDontShowNextButton(ctx, bundleIndex);
    const nextButtonVisible = selectNextButtonVisible(ctx, state, bundleIndex);

    return nextButtonVisible && !dontShowNextButton && !selectIsSingleRequiredFigmaBundle(ctx, bundleIndex);
};

export const selectShowNextButton = (ctx: SurveyStore, state: SurveyState): boolean => {
    const currentBundleIndex = selectCurrentBundleIndex(ctx);
    const dontShowNextButton = selectDontShowNextButton(ctx, currentBundleIndex);
    const nextButtonVisible = selectNextButtonVisible(ctx, state, currentBundleIndex);

    return nextButtonVisible && !dontShowNextButton && !selectIsSingleRequiredFigmaBundle(ctx, currentBundleIndex);
};

export const selectShowPrevButtonByBundleIndex = (
    ctx: SurveyStore,
    state: SurveyState,
    bundleIndex: number,
): boolean => {
    const prevButtonVisible = selectPrevButtonVisible(ctx, state, bundleIndex);
    const {
        params: {
            other: { backToQuestion },
        },
    } = ctx.info;

    return prevButtonVisible && backToQuestion;
};

export const selectShowPrevButton = (ctx: SurveyStore, state: SurveyState): boolean => {
    const currentBundleIndex = selectCurrentBundleIndex(ctx);
    const prevButtonVisible = selectPrevButtonVisible(ctx, state, currentBundleIndex);
    const {
        params: {
            other: { backToQuestion },
        },
    } = ctx.info;

    return prevButtonVisible && backToQuestion;
};

export const selectSendButtonVisible = (ctx: SurveyStore, state: SurveyState): boolean => {
    const currentBundle = selectCurrentBundle(ctx);
    const nextBundle = selectNextBundle(ctx);

    if (state === SurveyState.finishing) {
        return true;
    }

    if (!stateOrSubstate(state, SurveyState.bundles) || !currentBundle || currentBundle.type !== BundleType.QUESTIONS) {
        return false;
    }

    return !nextBundle || nextBundle.type !== BundleType.QUESTIONS;
};

export const selectSendNextButtonVisible = (ctx: SurveyStore, state: SurveyState, bundleIndex: number): boolean => {
    const currentBundle = selectBundleByIndex(ctx, bundleIndex);
    const nextBundle = selectBundleByIndex(ctx, bundleIndex + 1);

    if (state === SurveyState.finishing) {
        return true;
    }

    if (
        (!stateOrSubstate(state, SurveyState.bundles) && !stateOrSubstate(state, SurveyState.phoneAuth)) ||
        !currentBundle ||
        currentBundle.type !== BundleType.QUESTIONS
    ) {
        return false;
    }

    return !nextBundle || nextBundle.type !== BundleType.QUESTIONS;
};

export const selectShowSendButtonByBundleIndex = (
    ctx: SurveyStore,
    state: SurveyState,
    bundleIndex: number,
): boolean => {
    const sendButtonVisible = selectSendNextButtonVisible(ctx, state, bundleIndex);
    const {
        params: {
            main: { phoneAuth },
        },
    } = selectInfo(ctx);
    const dontShowNextButton = !phoneAuth && selectDontShowNextNextButton(ctx, bundleIndex);

    if (ctx.stopReason === BundleFinishType.STOP_BY_TIMER) {
        return ctx.info.params.main.phoneAuth && state === SurveyState.phoneAuth ? true : false;
    }

    return sendButtonVisible && !dontShowNextButton && !selectIsSingleRequiredFigmaBundle(ctx, bundleIndex);
};
export const selectShowSendButton = (ctx: SurveyStore, state: SurveyState): boolean => {
    const currentBundleIndex = selectCurrentBundleIndex(ctx);
    const sendButtonVisible = selectSendNextButtonVisible(ctx, state, currentBundleIndex);
    const dontShowNextButton = selectDontShowNextNextButton(ctx, currentBundleIndex);

    if (isPreview && isAllPagesOnOnePage && stateOrSubstate(state, SurveyState.bundles)) return true;
    if (ctx.stopReason === BundleFinishType.STOP_BY_TIMER) {
        return ctx.info.params.main.phoneAuth && state === SurveyState.phoneAuth ? true : false;
    }

    return sendButtonVisible && !dontShowNextButton && !selectIsSingleRequiredFigmaBundle(ctx, currentBundleIndex);
};

export const selectPrevBundle = (ctx: SurveyStore): BundleEntity => {
    const { bundles, currentBundleIndex } = ctx;
    const isFirstPage = selectIsFirstBundle(ctx);

    if (bundles.length === 0 || isFirstPage) {
        return null;
    }

    return bundles[currentBundleIndex - 1];
};

export const selectShowIndicator = ({ info, timerRef }: SurveyStore, state): boolean => {
    const {
        params: {
            other: { indicator },
        },
    } = info;

    return Boolean((indicator || timerRef) && state !== SurveyState.finished && state !== SurveyState.testResults);
};

export const selectShowHeader = (ctx: SurveyStore, state: SurveyState): boolean => {
    const isFirstPage = selectIsFirstBundle(ctx);
    const withClickReply = selectWithClickReplyWithTwist(ctx);
    const {
        info: {
            design: {
                brand: { headerAllPage, headerType },
            },
        },
    } = ctx;

    if (
        withClickReply &&
        [SurveyState.bootstrap, SurveyState.fetchContent, SurveyState.bundles, SurveyState.finishing].includes(state)
    )
        return false;

    return Boolean(
        headerType !== HeaderBackgroundType.NOT_HEAD && (!isPreview || forceHeader) && (headerAllPage || isFirstPage),
    );
};

export const selectShowHeaderInBody = (ctx: SurveyStore, state: SurveyState): boolean => {
    const withClickReply = selectWithClickReplyWithTwist(ctx);
    if (
        withClickReply &&
        [SurveyState.bootstrap, SurveyState.fetchContent, SurveyState.bundles, SurveyState.finishing].includes(state)
    )
        return false;
    return ctx.info.design.brand.headerTitlePosition === HeaderTitlePosition.UNDER_HEAD && !!ctx.info.title;
};
export const selectIsStartPage = (_: SurveyStore, state: SurveyState): boolean => state === SurveyState.welcomePage;

export const selectWelcomePageText = ({ info }: SurveyStore): string => {
    const {
        params: {
            main: { welcomePageText },
        },
    } = info;

    return welcomePageText;
};

export const selectWelcomePage = ({ info }: SurveyStore): WelcomePageType | null => {
    const {
        params: {
            main: { welcomePage },
        },
    } = info;

    return welcomePage;
};

export const selectShowWelcomeText = (ctx: SurveyStore, state: SurveyState): boolean => {
    const withClickReply = selectWithClickReplyWithTwist(ctx);
    const welcomePageText = selectWelcomePageText(ctx);
    const welcomePage = selectWelcomePage(ctx);
    const isFirstBundle = selectIsFirstBundle(ctx);
    const showToSurveyPage =
        isFirstBundle &&
        state === SurveyState.bundles &&
        !withClickReply &&
        welcomePage === WelcomePageType.TO_SURVEY_PAGE;
    const showToSinglePage =
        state === SurveyState.welcomePage &&
        (withClickReply ? welcomePage !== WelcomePageType.NOT : welcomePage === WelcomePageType.TO_SINGLE_PAGE);

    return Boolean(welcomePageText && (showToSurveyPage || showToSinglePage));
};
export const selectShowWelcomePage = (ctx: SurveyStore, state: SurveyState): boolean => {
    // const isStartPage = selectIsFirstBundle(ctx);
    const showWelcomeText = selectShowWelcomeText(ctx, state);
    return showWelcomeText;
};

export const selectWithClickReply = (ctx: SurveyStore): boolean => {
    return ctx.info.params.other.clickReply && !isAllPagesOnOnePage;
};

export const selectWithClickReplyWithTwist = (ctx: SurveyStore): boolean => {
    return ctx.info.params.other.clickReply && !isEmbedded && !isAllPagesOnOnePage;
};

export const selectFirstInvalidId = (ctx: SurveyStore): number | null => {
    const currentBundle = selectCurrentBundle(ctx);

    if (!currentBundle) return null;

    const firstInvalid = currentBundle.questions.find((questionRef) => {
        const q = getQ(questionRef);
        return q.invalid || q.commentInvalid;
    });

    return firstInvalid ? firstInvalid.getSnapshot().context.id : null;
};

export const selectStartLoading = ({ loading: { fetch, init, load } }: SurveyStore): boolean => fetch || load || init;

export const selectSendLoading = (_ctx: SurveyStore, state: SurveyState): boolean => state === SurveyState.finishing;

export const selectShowConfirmSms = (ctx: SurveyStore) => ctx.showConfirmSms;

export const selectNextLoading = ({ loading: { next } }: SurveyStore): boolean => next;

export const selectSurveyProgress = ({ questions }: SurveyStore): number => calcSurveyProgress(questions);

export const selectInvalidQuestionsQuantity = ({ questions }: SurveyStore): number =>
    questions.reduce((acc, questionRef) => acc + (questionRef.getSnapshot().context.invalid ? 1 : 0), 0);

export const selectTimerRef = ({ timerRef }: SurveyStore) => timerRef;

export const selectTimeAgo = ({ info, timerRef, endDatetime, startDatetime }: SurveyStore): Duration => {
    const now = dayjs();
    const {
        main: { timerHours, timerMinutes, timerSeconds },
    } = info.params;
    const allTime = now.add(timerHours, 'h').add(timerMinutes, 'm').add(timerSeconds, 's');

    if (!timerRef) {
        // TODO strict check
        // fixes reading value if timer not set
        if (endDatetime && startDatetime) {
            return dayjs.duration(endDatetime.diff(startDatetime));
        }
        return dayjs.duration(0);
    }

    const endTime = now.add(
        timerRef.getSnapshot().context.duration * 1000 - timerRef.getSnapshot().context.timeAgo,
        'ms',
    );

    return dayjs.duration(allTime.diff(endTime));
};

export const selectAffectedQuestions = ({ questions }: SurveyStore): Array<QuestionActorRef> => {
    return questions;
    // @ts-ignore
    const withLogicTriggered = questions.filter(({ machine: { context: shownByLogic } }) => shownByLogic);
    // @ts-ignore
    return withLogicTriggered.length > 0 ? withLogicTriggered : questions;
};

export const selectCompletePageTextWithPromocodes = ({
    info: {
        params: {
            main: { completePageText, quotaFilledText, quotaFilledStartText },
        },
    },
    promocode,
    quotaDisqual,
    quotaFilled,
}: SurveyStore): string => {
    // Общая квота опроса набрана: свой текст для «не успел начать» и свой для
    // «набралась, пока проходил»; иначе — стандартная страница завершения.
    const text = quotaDisqual ? quotaFilledText : quotaFilled ? quotaFilledStartText : completePageText;

    return text ? text.replace(/{promocode}/gi, promocode ?? '') : null;
};

export const selectInfo = ({ info }: SurveyStore): InfoModel => {
    return info;
};

export const selectFactPoints = ({ factPoints }: SurveyStore): number => factPoints;

export const selectSurveyPages = ({ pages }: SurveyStore): Array<PageModel> => pages;

export const selectShowCompletePage = (context: SurveyStore, state: SurveyState): boolean => {
    const stopReason = context.stopReason;
    const currentBundle = selectCurrentBundle(context);

    if (stopReason === BundleFinishType.DISQUAL && currentBundle.type === BundleType.TEXT) {
        return false;
    }
    if (stopReason === BundleFinishType.TEXT || (currentBundle?.type === BundleType.TEXT && currentBundle.text)) {
        return false;
    }
    return state === SurveyState.finished || state === SurveyState.completed;
};

export const selectIsDetailsLoading = (ctx: SurveyStore) => ctx.loading.details;
export const selectIsQuotasLoading = (ctx: SurveyStore) => ctx.loading.quotas;
export const selectShowBundles = (ctx: SurveyStore, st: SurveyState): boolean => {
    const currentBundle = selectCurrentBundle(ctx);
    const bundlesState =
        st === SurveyState.phoneAuth ||
        st === SurveyState.bundles ||
        st === SurveyState.finishing ||
        stateOrSubstate(st, SurveyState.bundles);
    const showWelcomeText = selectShowWelcomeText(ctx, st);
    const welcomePage = selectWelcomePage(ctx);
    const stopReason = ctx.stopReason;

    if (stopReason === BundleFinishType.STOP_BY_TIMER) {
        return ctx.info.params.main.phoneAuth && st === SurveyState.phoneAuth ? true : false;
    }

    if (stopReason === BundleFinishType.DISQUAL && currentBundle.type === BundleType.TEXT) {
        return true;
    }
    if (stopReason === BundleFinishType.TEXT || currentBundle?.type === BundleType.TEXT) {
        return true;
    }
    if (showWelcomeText && welcomePage === WelcomePageType.TO_SINGLE_PAGE) {
        return false;
    }
    return Boolean(bundlesState && currentBundle);
};

export const selectDomain = ({ domain }: SurveyStore): string => domain;

export const selectToken = ({ token }: SurveyStore): string => token;

export const selectScrollToQuestionId = ({ scrollToQuestionId }: SurveyStore): number => scrollToQuestionId;

export const selectInitialLoading = (_: SurveyStore, state: SurveyState): boolean => state === SurveyState.idle;

export const selectInitialLoadingOrBootstrap = (_: SurveyStore, state: SurveyState): boolean =>
    state === SurveyState.idle || state === SurveyState.bootstrap;

export const selectPreviousQuestionById = (
    ctx: SurveyStore,
    currentOrder: number,
    findId?: number,
): QuestionContext | null => {
    const { questions } = ctx;
    const findQuestion = questions.find((q) => q.getSnapshot().context.id === findId);

    if (findQuestion) {
        const qContext = findQuestion.getSnapshot().context;

        if (qContext.order < currentOrder) {
            return qContext;
        }
    }
    return null;
};

export const selectShowNav = (_: SurveyStore, state: SurveyState): boolean => state !== SurveyState.testResults;

export const selectTestResult = (_: SurveyStore, state: SurveyState): boolean => state === SurveyState.testResults;
export const selectStatusMessage = (ctx: SurveyStore, state: SurveyState): string => {
    const {
        params: {
            main: { disabledSurveyText, surveyWasBlocked },
        },
    } = selectInfo(ctx);

    return state === SurveyState.blocked ? surveyWasBlocked : disabledSurveyText;
};

export const selectRetryNotAllowedMessage = (ctx: SurveyStore): string => {
    const {
        params: {
            main: { retryDisabledSurveyText },
        },
    } = selectInfo(ctx);

    return retryDisabledSurveyText;
};

export const selectErrorText = ({ errorText }: SurveyStore): string =>
    errorText || 'Упс... Что-то пошло не так. Попробуйте обновить страницу.';

const isAnyOfQuestionsHaveTimerSelector = (ctx: SurveyStore): boolean =>
    ctx.questions.some((q) => Boolean(q.getSnapshot().context.params.timer));

export const selectSaveIncomplete = (ctx: SurveyStore): boolean => {
    const {
        params: {
            main: { timer: timerForSurvey, timerOn: timerAfterQuestionId },
        },
        savingNotComplete,
    } = selectInfo(ctx);
    const isAnyOfQuestionsHaveTimer = isAnyOfQuestionsHaveTimerSelector(ctx);

    return (
        (savingNotComplete || !!timerAfterQuestionId || !!timerForSurvey || isAnyOfQuestionsHaveTimer) &&
        !isOfflineMode()
    );
};

export const selectIsWithAudioRecogn = (ctx: SurveyStore): boolean => ctx.info.params.other.audioRecogn;
